import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { useAIChat } from '../../contexts/AIChatContext';

const FloatingAIChat = () => {
  const { isVisible } = useAIChat();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: 'Olá! Sou o Dai, seu mentor de aprendizagem. Como posso ajudá-lo hoje?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');

  if (!isVisible) {
    return null;
  }

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = () => {
    if (!inputMessage?.trim()) return;

    const newMessage = {
      id: messages?.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: messages?.length + 2,
        type: 'ai',
        content: 'Entendi sua pergunta. Deixe-me ajudá-lo com isso...',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-200">
        <Button
          onClick={handleToggleChat}
          variant="default"
          className="w-14 h-14 rounded-full shadow-tier-2 hover:shadow-tier-3 transition-all duration-300"
          iconName={isOpen ? "X" : "MessageCircle"}
          iconSize={24}
        />
        
        {/* Notification Badge */}
        {!isOpen && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-secondary rounded-full flex items-center justify-center">
            <span className="text-xs font-medium text-secondary-foreground">1</span>
          </div>
        )}
      </div>
      {/* Chat Interface */}
      {isOpen && (
        <>
          {/* Mobile Overlay */}
          <div className="md:hidden fixed inset-0 bg-background z-300">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-card">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="Bot" size={20} color="var(--color-accent-foreground)" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-card-foreground">Mentor Dai</h3>
                    <p className="text-sm text-muted-foreground font-caption">Assistente de IA</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleToggleChat}
                  iconName="X"
                  iconSize={20}
                />
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages?.map((message) => (
                  <div
                    key={message?.id}
                    className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        message?.type === 'user' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground border border-border'
                      }`}
                    >
                      <p className="text-sm">{message?.content}</p>
                      <p className="text-xs opacity-70 mt-1 font-caption">
                        {message?.timestamp?.toLocaleTimeString('pt-BR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-border bg-card">
                <div className="flex space-x-2">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 resize-none p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={1}
                  />
                  <Button
                    onClick={handleSendMessage}
                    variant="default"
                    iconName="Send"
                    iconSize={18}
                    className="px-4"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Panel */}
          <div className="hidden md:block fixed bottom-24 right-6 w-96 h-[500px] bg-card border border-border rounded-lg shadow-tier-3 z-300 backdrop-blur-modal">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="Bot" size={16} color="var(--color-accent-foreground)" />
                  </div>
                  <div>
                    <h3 className="font-heading font-medium text-card-foreground">Mentor Dai</h3>
                    <p className="text-xs text-muted-foreground font-caption">Online</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleToggleChat}
                  iconName="Minus"
                  iconSize={16}
                  className="p-1"
                />
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages?.map((message) => (
                  <div
                    key={message?.id}
                    className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] p-3 rounded-lg text-sm ${
                        message?.type === 'user' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      <p>{message?.content}</p>
                      <p className="text-xs opacity-70 mt-1 font-caption">
                        {message?.timestamp?.toLocaleTimeString('pt-BR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-border">
                <div className="flex space-x-2">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 resize-none p-2 text-sm border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={1}
                  />
                  <Button
                    onClick={handleSendMessage}
                    variant="default"
                    iconName="Send"
                    iconSize={16}
                    className="px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FloatingAIChat;